import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { House, File } from 'phosphor-react';
import { AuthEventData } from '@aws-amplify/ui';
import { AmplifyUser } from '@aws-amplify/ui/dist/types/types/authenticator/user';
import { useAuthenticator } from '@aws-amplify/ui-react';

import { ROOT_PATH, HOW_TO_FILE_PATH } from 'constants/paths';
import { EMPLOYEE_REPORT_TYPE, CONTRACTOR_REPORT_TYPE } from 'constants/common';
import ContactSupportButton from 'components/contact-support-button';
import { NavigationItemInterface } from 'types/layout';
import DesktopSidebar from 'components/layout/sidebar-layout/desktop-sidebar';
import MobileSidebar from 'components/layout/sidebar-layout/mobile-sidebar';
import StickyHeader from 'components/layout/sidebar-layout/sticky-header';
import { Report } from 'graphql-api';
import { getReports } from 'api/reports';

function SidebarLayout({
  user,
  signOut
}: {
  user: AmplifyUser | undefined;
  signOut: ((data?: AuthEventData | undefined) => void) | undefined;
}): JSX.Element {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [employeeReport, setEmployeeReport] = useState<Report | null>(null);
  const [contractorReport, setContractorReport] = useState<Report | null>(null);
  const { route } = useAuthenticator((context) => [context.route]);
  const hasCompletedEmployeeReport = employeeReport?.paid === true;
  const hasCompletedContractorReport =
    contractorReport?.paid === true && contractorReport?.resultGenerated === true;

  useEffect(() => {
    if (route !== 'authenticated') return;

    void (async () => {
      const fetchedReports = await getReports();
      if (fetchedReports.length > 0) {
        setEmployeeReport(
          fetchedReports.filter((report) => report.reportType === EMPLOYEE_REPORT_TYPE)[0]
        );
        setContractorReport(
          fetchedReports.filter((report) => report.reportType === CONTRACTOR_REPORT_TYPE)[0]
        );
      }
      setIsLoading(false);
    })();
  }, []);

  if (isLoading) {
    return <></>;
  }

  const navigation: NavigationItemInterface[] = [
    { name: 'Home', href: ROOT_PATH, icon: House, current: location.pathname === ROOT_PATH }
  ];

  if (hasCompletedEmployeeReport || hasCompletedContractorReport) {
    navigation.push({
      name: 'How to File',
      href: HOW_TO_FILE_PATH,
      icon: File,
      current: location.pathname === HOW_TO_FILE_PATH
    });
  }
  const companyName = user?.attributes?.['custom:company_name'] ?? '';
  const userName = `${user?.attributes?.given_name ?? ''} ${user?.attributes?.family_name ?? ''}`;
  const nameInitial = `${user?.attributes?.given_name?.[0] ?? ''}${
    user?.attributes?.family_name?.[0] ?? ''
  }`;

  return (
    <main>
      <MobileSidebar
        navigation={navigation}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />
      <DesktopSidebar
        navigation={navigation}
        userName={userName}
        companyName={companyName}
        nameInitial={nameInitial}
        signOut={signOut}
      />
      <div className="h-screen flex flex-col lg:pl-64">
        <StickyHeader nameInitial={nameInitial} setSidebarOpen={setSidebarOpen} signOut={signOut} />
        <div className="flex-1 bg-white pb-24">
          <Outlet context={{ employeeReport, contractorReport }} />
        </div>
      </div>
      {user != null && <ContactSupportButton />}
    </main>
  );
}

export default SidebarLayout;
